import {
  createContext,
  useCallback,
  useContext,
  useLayoutEffect,
  useState,
  type ReactNode,
} from 'react';

import { useAgentChat } from '../hooks/data/useAgentChat';

/**
 * One chat session for the whole app, so the conversation outlives the panel
 * that shows it.
 *
 * ── Why this exists ──────────────────────────────────────────────────
 * `useAgentChat` holds the transcript, the streaming flag and the in-flight
 * turn's refs in component state. Called from `BirdChatPanel`, that state has
 * the PANEL's lifetime — and the panel is unmounted outright whenever the
 * left-panel slot goes to 'none' (collapse) or another panel takes it, and
 * again on every mobile route change away from `/m/bird`.
 *
 * So collapsing the chat mid-answer destroyed the turn: the user's message,
 * the assistant placeholder and `isStreaming` all went with the unmount, the
 * SSE reader carried on writing into a dead component, and reopening re-seeded
 * from the `session-history` cache entry captured BEFORE the send (fresh for
 * its 60s staleTime, because only a terminal event invalidates it). The
 * researcher saw an empty panel, and the answer reappeared only once something
 * happened to re-read history.
 *
 * Mounted above `Layout`, the hook's state now has the SESSION's lifetime: the
 * stream keeps filling it while the panel is closed, and reopening is a pure
 * re-render of state that never went away. Nothing in `useAgentChat` changes —
 * the defences it already carries for a genuine reload (re-seeding, the
 * invalidate-on-every-terminal-outcome rule) are untouched and still cover the
 * paths that DO remount it: a page reload, a session switch, a workspace
 * switch.
 *
 * ── Why it starts inactive ───────────────────────────────────────────
 * A provider at the app root runs for every page, including for users who
 * never open chat. `enabled` is what keeps that free: until a chat surface
 * mounts, the history fetch and the connector lookup are both disabled, so an
 * app load costs no agent-service request it didn't cost before. The first
 * surface to call `useAgentChatSession` flips it on, and it stays on for the
 * rest of the session — de-activating on unmount is exactly the bug above.
 */
const AgentChatStateContext = createContext<ReturnType<typeof useAgentChat> | null>(null);

/**
 * Split from the state context on purpose: this value must be STABLE, because
 * consumers call it from a mount effect. Folded into the state object it would
 * change identity on every streamed token, and every consumer would re-run its
 * activation effect on every frame of an answer.
 */
const AgentChatActivateContext = createContext<() => void>(() => undefined);

export function AgentChatProvider({ children }: { readonly children: ReactNode }) {
  const [isActive, setIsActive] = useState(false);
  const activate = useCallback(() => setIsActive(true), []);
  const chat = useAgentChat({ enabled: isActive });

  return (
    <AgentChatActivateContext.Provider value={activate}>
      <AgentChatStateContext.Provider value={chat}>{children}</AgentChatStateContext.Provider>
    </AgentChatActivateContext.Provider>
  );
}

export function useAgentChatSession(): ReturnType<typeof useAgentChat> {
  const activate = useContext(AgentChatActivateContext);
  const state = useContext(AgentChatStateContext);
  // `useLayoutEffect`, not `useEffect`: a passive effect runs AFTER paint, so
  // the render that mounts a chat surface would commit with the session still
  // inactive — query disabled, `isLoadingHistory` false, `messages` empty,
  // which is exactly what the panel renders as "Start a conversation with
  // Bower". A returning researcher would see that over their own transcript
  // for a frame. jsdom cannot be the oracle here (RTL flushes passive effects
  // inside `render`, so the frame is invisible to a test either way) — this
  // removes the frame rather than arguing about whether it paints.
  //
  // It also moves activation ahead of `BirdChatPanel`'s handover phase 2, which
  // gates on `isLoadingHistory`. No behaviour change today (that gate already
  // waits out the fetch), but it is why a `send` handover cannot reach a
  // `sendMessage` that would no-op on `!enabled`.
  //
  // Braces, not an implicit return: an effect callback that returns a value
  // React reads as a cleanup function, so the day `activate` stops returning
  // void this silently becomes "call the result on unmount" and throws.
  useLayoutEffect(() => {
    activate();
  }, [activate]);
  if (!state) {
    throw new Error('useAgentChatSession must be used within an AgentChatProvider');
  }
  return state;
}
